/** A stored letter's HTML, made inert for the email log's preview frame (#6922). */
export type EmailPreviewDocument = {
  /** The whole document, for an iframe's `srcDoc`. */
  html: string;
  /** How many images the letter would fetch from another host. */
  remoteImageCount: number;
};

// Elements that load, run or redirect something. The frame's sandbox already stops scripts; these
// go too, so nothing the letter carries can reach the network or move the frame.
const REMOVED_ELEMENTS = "script, noscript, iframe, frame, frameset, object, embed, applet, base, link, meta, form";
const REMOTE_URL = /^\s*(https?:)?\/\//i;

/**
 * The letter as a document that can do nothing but show itself.
 *
 * The frame that shows it must be `sandbox=""`, which forbids scripts, forms, pop-ups and
 * navigating the page. On top of that, the document carries its own Content-Security-Policy, so
 * it fetches nothing at all, not even an image, unless `allowRemoteImages` is set. A remote image
 * is how a letter tells its sender it was opened. Every link opens a new window, which the
 * sandbox refuses, so a click goes nowhere and the frame stays on the letter.
 */
export function buildEmailPreviewDocument(html: string, options: { allowRemoteImages: boolean }): EmailPreviewDocument {
  const document = new DOMParser().parseFromString(html, "text/html");
  for (const element of Array.from(document.querySelectorAll(REMOVED_ELEMENTS))) element.remove();
  for (const element of Array.from(document.querySelectorAll("*"))) removeActiveAttributes(element);
  for (const anchor of Array.from(document.querySelectorAll("a"))) {
    anchor.setAttribute("target", "_blank");
    anchor.setAttribute("rel", "noopener noreferrer");
  }

  const remoteImageCount = Array.from(document.querySelectorAll("img")).filter((image) =>
    REMOTE_URL.test(image.getAttribute("src") ?? ""),
  ).length;

  const policy = document.createElement("meta");
  policy.setAttribute("http-equiv", "Content-Security-Policy");
  policy.setAttribute("content", contentSecurityPolicy(options.allowRemoteImages));
  document.head.prepend(policy);

  return { html: `<!DOCTYPE html>${document.documentElement.outerHTML}`, remoteImageCount };
}

function contentSecurityPolicy(allowRemoteImages: boolean): string {
  const images = allowRemoteImages ? "img-src data: https: http:" : "img-src data:";
  return [
    "default-src 'none'",
    "style-src 'unsafe-inline'",
    images,
    "font-src data:",
    "form-action 'none'",
    "base-uri 'none'",
  ].join("; ");
}

function removeActiveAttributes(element: Element): void {
  for (const attribute of Array.from(element.attributes)) {
    const name = attribute.name.toLowerCase();
    if (name.startsWith("on") || name === "srcdoc" || name === "formaction") {
      element.removeAttribute(attribute.name);
      continue;
    }
    if (isUrlAttribute(name) && /^\s*(javascript|vbscript|data:text\/html)/i.test(attribute.value)) {
      element.removeAttribute(attribute.name);
    }
  }
}

function isUrlAttribute(name: string): boolean {
  return name === "href" || name === "src" || name === "action" || name === "xlink:href" || name === "background";
}
